import React, { useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, Chip, IconButton, Stack, Step, StepContent, StepLabel,
    Stepper, TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendToMobileOutlinedIcon from '@mui/icons-material/SendToMobileOutlined';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import orderServices from 'app/services/order-services';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { getDropoffCarrierByKey, getDropoffCarrierDisplay, getDropoffCarrierOptions } from 'app/utils/dropoffCarriers';
import AddressAutocomplete from 'app/widgets/Shared/AddressAutocomplete';

/**
 * Plan Distribuidor: "Enviar a preparación (App móvil)". Mismos pasos que el dropoff en varios
 * pedidos del viaje (pedidos, carrier, punto), pero la dirección se escribe en vez de elegir un
 * punto en el mapa, y se elige el día en que aparece en la app.
 *
 * La selección vive en OrdersList (los checkboxes están en las filas) para que no se pierda al
 * cambiar de página; acá solo se muestra y se manda.
 *   selected: [{ id, code, dropoff_carrier, shipping_method }]
 */

// No es un carrier del catalogo: el pedido se prepara sin dropoff ni direccion
// (Distribuidor::EnvioPreparacion::RETIRO_EN_TIENDA).
const RETIRO_EN_TIENDA = { value: 'retiro_tienda', label: 'Retiro en tienda' };
const SendToPreparationPanel = ({ selected, onRemove, onClear, onClose, onSent }) => {
    const { companySettings } = useCompanySettings() || {};
    const carrierOptions = useMemo(
        () => [RETIRO_EN_TIENDA, ...getDropoffCarrierOptions(companySettings?.available_dropoff_carriers)],
        [companySettings?.available_dropoff_carriers]
    );
    const { enqueueSnackbar } = useSnackbar();

    const [step, setStep] = useState(0);
    const [carrier, setCarrier] = useState(null);
    const [address, setAddress] = useState('');
    const [shipDate, setShipDate] = useState(() => moment().format('YYYY-MM-DD'));
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);

    // Direccion por defecto del carrier, la que la compañia configura en Editar compañia.
    const defaultAddressOf = (c) =>
        (c ? getDropoffCarrierByKey(c.value, companySettings?.available_dropoff_carriers)?.defaultAddress?.text : '') || '';

    // Al elegir carrier la direccion parte con la suya por defecto. Si ya se habia escrito
    // otra a mano no se pisa: solo se reemplaza si estaba vacia o era la del carrier anterior.
    const changeCarrier = (next) => {
        if (!address.trim() || address === defaultAddressOf(carrier)) setAddress(defaultAddressOf(next));
        setCarrier(next);
    };

    const isPickup = carrier?.value === RETIRO_EN_TIENDA.value;
    const canSend = selected.length > 0 && carrier && (isPickup || address.trim().length >= 3) && shipDate;

    // Con quien salia cada pedido antes del envio: su courier, o retiro en tienda.
    const currentShippingOf = (o) => {
        if (o.dropoff_carrier) return { value: o.dropoff_carrier, label: getDropoffCarrierDisplay(o.dropoff_carrier, companySettings?.available_dropoff_carriers)?.label };
        if (String(o.shipping_method || '').toLowerCase() === 'pickup') return RETIRO_EN_TIENDA;
        return null;
    };
    // Pedidos a los que el carrier elegido les cambia el que ya traian (Bluexpress de Shopify,
    // por ejemplo): el envio se los pisa, asi que se avisan antes de confirmar.
    const overwritten = carrier
        ? selected
            .map((o) => ({ ...o, current: currentShippingOf(o) }))
            .filter((o) => o.current && o.current.value !== carrier.value)
        : [];

    // No se puede saltar a un paso sin completar los anteriores.
    const goTo = (target) => {
        if (target >= 1 && selected.length === 0) return;
        if (target >= 2 && !carrier) return;
        setStep(target);
    };

    const send = () => {
        setSending(true);
        orderServices.sendToPreparation({
            order_ids: selected.map((o) => o.id),
            dropoff_carrier: carrier.value,
            dropoff_address: isPickup ? '' : address.trim(),
            ship_date: shipDate,
        })
            .then((data) => {
                enqueueSnackbar(
                    `${data?.order_ids?.length || selected.length} pedidos enviados a la app para el ${moment(shipDate).format('DD MMM')}`,
                    { variant: 'success' }
                );
                setStep(0);
                setCarrier(null);
                setAddress('');
                onSent(data);
            })
            .catch((err) => {
                enqueueSnackbar(err?.response?.data?.error || 'No fue posible enviar los pedidos', { variant: 'error' });
            })
            .finally(() => {
                setSending(false);
                setConfirmOpen(false);
            });
    };

    return (
        <Card sx={{ mb: 2, p: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                        <SendToMobileOutlinedIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Enviar a preparación
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Los pedidos aparecen en la app móvil, en Pedidos (preparación y pickeo).
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Typography variant="body2">Pedidos</Typography>
                <Chip size="small" color="primary" label={`${selected.length} seleccionados`} />
                {selected.length > 0 && (
                    <Button size="small" onClick={onClear}>Quitar todos</Button>
                )}
            </Stack>

            <Stepper activeStep={step} orientation="vertical" sx={{ mt: 1 }}>
                <Step completed={step > 0}>
                    <StepLabel sx={{ cursor: 'pointer' }} onClick={() => goTo(0)}>Pedidos a enviar</StepLabel>
                    <StepContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Marca los pedidos con la casilla a la derecha de cada uno. Puedes cambiar de
                            página o filtrar: lo marcado se mantiene.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, maxHeight: 160, overflowY: 'auto' }}>
                            {selected.map((o) => (
                                <Chip key={o.id} size="small" label={`#${o.code}`} onDelete={() => onRemove(o.id)} />
                            ))}
                        </Box>
                        <Button variant="contained" size="small" disabled={selected.length === 0} onClick={() => goTo(1)}>
                            Continuar
                        </Button>
                    </StepContent>
                </Step>

                <Step completed={step > 1}>
                    <StepLabel sx={{ cursor: 'pointer' }} onClick={() => goTo(1)} optional={carrier && step !== 1 ? <Typography variant="caption">{carrier.label}</Typography> : null}>
                        Carrier
                    </StepLabel>
                    <StepContent>
                        <Autocomplete
                            size="small"
                            options={carrierOptions}
                            value={carrier}
                            onChange={(e, v) => changeCarrier(v)}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            renderInput={(params) => <TextField {...params} label="¿Quién despacha?" />}
                            sx={{ mb: 1 }}
                        />
                        <Button variant="contained" size="small" disabled={!carrier} onClick={() => goTo(2)}>
                            Continuar
                        </Button>
                    </StepContent>
                </Step>

                <Step>
                    <StepLabel sx={{ cursor: 'pointer' }} onClick={() => goTo(2)}>{isPickup ? 'Día' : 'Dirección y día'}</StepLabel>
                    <StepContent>
                        {/* El mismo buscador de Editar compañia: sugiere direcciones desde la 3a letra. */}
                        {!isPickup && (
                            <Box sx={{ mb: 1.5 }}>
                                <AddressAutocomplete
                                    label={`Dirección de entrega a ${carrier?.label || 'el carrier'}`}
                                    placeholder="Ej: Av. Providencia 1234, Providencia"
                                    value={address}
                                    onChange={setAddress}
                                    helperText={address && address === defaultAddressOf(carrier) ? 'Dirección por defecto del carrier' : undefined}
                                />
                            </Box>
                        )}
                        <TextField
                            size="small"
                            type="date"
                            label="Día en la app"
                            value={shipDate}
                            onChange={(e) => setShipDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="La app muestra los pedidos del día elegido."
                            sx={{ mb: 1 }}
                        />
                        {selected.length === 0 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>Marca al menos un pedido.</Alert>
                        )}
                        <Box>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={!canSend || sending}
                                startIcon={<SendToMobileOutlinedIcon />}
                                onClick={() => setConfirmOpen(true)}
                            >
                                Enviar a la app
                            </Button>
                        </Box>
                    </StepContent>
                </Step>
            </Stepper>

            <ConfirmDialog
                open={confirmOpen}
                title={overwritten.length > 0 ? '¿Sobrescribir el carrier?' : 'Enviar a preparación'}
                message={<>
                    <strong>{selected.length} {selected.length === 1 ? 'pedido' : 'pedidos'}</strong> aparecerán en la app móvil
                    el <strong>{moment(shipDate).format('DD [de] MMMM')}</strong>
                    {isPickup ? <>, como <strong>retiro en tienda</strong>.</> : <>, con dropoff en <strong>{carrier?.label}</strong>:<br />{address}</>}
                    {overwritten.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            {overwritten.length === 1 ? 'Este pedido ya tenía' : 'Estos pedidos ya tenían'} otro carrier.
                            ¿Estás seguro de que deseas cambiarlo por <strong>{carrier?.label}</strong>?
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2, maxHeight: 160, overflowY: 'auto' }}>
                                {overwritten.map((o) => (
                                    <li key={o.id}>#{o.code}: {o.current.label} → <strong>{carrier?.label}</strong></li>
                                ))}
                            </Box>
                        </Alert>
                    )}
                </>}
                confirmText={overwritten.length > 0 ? 'Sobrescribir y enviar' : 'Enviar'}
                confirmColor="success"
                loading={sending}
                onConfirm={send}
                onClose={() => setConfirmOpen(false)}
            />
        </Card>
    );
};

export default SendToPreparationPanel;
